import TerminalsInfoWithFullIncludedAllowance from '@components/pages/Products/components/RouteProduct/components/TerminalsInfo/components/TerminalsInfoWithFullIncludedAllowance';
import { getFullPriceFromTerminals } from '@components/pages/Products/components/RouteProduct/components/TerminalsInfo/helpers';
import getIncludedAllowance from '@components/pages/Products/components/RouteProduct/components/TerminalsInfo/mocks';
import { WithRouteProductContextData } from '@components/pages/Products/components/RouteProduct/types';
import WithRouteProductContext from '@components/pages/Products/components/RouteProduct/WithRouteProductContext';
import { TaxNameProps } from '@helpers/getTaxNames';
import { MappedTerminalData } from '@helpers/getTerminalsFromStep';
import { RouteWidgetBaseParameters } from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import TerminalsInfoWithAllowance from '@pages/Basket/OrderList/components/OfferDetail/Products/RouteProduct/TerminalsInfo/components/TerminalsInfoWithAllowances';
import { RowProps } from '@services/collectors/ProductService/ShoulderItemDetailsDTO';
import { LocalizedMessage } from '@services/requests/localizedMessagesService/interfaces';
import { Order } from '@services/requests/orderService/interface';
import { CalculatedAllowanceOffer } from '@services/requests/orderService/types';
import { BranchItem } from '@services/requests/routeCalculator/locationsBranchSearchService/interfaces';
import { AllowanceData } from '@services/requests/searchLoaders/allowanceLoader/AllowanceLoaderQuery';
import { CurrencyData } from '@services/requests/searchLoaders/currencyLoader/CurrencyLoaderQuery';
import { getLocalization } from '@settings/services/helpers/getLocalization';
import React from 'react';
import { useTranslation } from 'react-i18next';

type TerminalsGroupInfoProps = WithRouteProductContextData<{
  terminalGroup: MappedTerminalData[];
  terminals: BranchItem[];
  taxNames: TaxNameProps[];
  endTransportingConditionId: number;
  startTransportingConditionId: number;
  order: Order;
  realTimeBaseData: RouteWidgetBaseParameters;
  isUseActualCourses: boolean;
}>;

/**
 * Компонент выводит данные по одному терминалу. На вход принимает все
 * расходы, которые были по этому терминалу.
 */
const TerminalsGroupInfo = WithRouteProductContext((props: TerminalsGroupInfoProps) => {
  const {
    currencyRoute,
    terminalGroup,
    terminals,
    primaryLanguageId,
    realTimeBaseData: { allowancesData = [], unitGroups = [] },
    taxNames,
    endTransportingConditionId,
    startTransportingConditionId,
    order,
    isUseActualCourses,
    productData,
  } = props;

  const { t } = useTranslation('RoutesListComponent');

  const crossDockingOperations =
    productData.route.routes[0].mixedTransport?.crossDockingOperations ?? [];

  const [terminal] = terminalGroup;
  const totalPrice = getFullPriceFromTerminals(terminalGroup, crossDockingOperations);
  const firstTerminal = terminalGroup.find((t) => t.isFirst);
  const lastTerminal = terminalGroup.find((t) => t.isLast);
  const currencies = isUseActualCourses
    ? props.realTimeBaseData.currencies
    : props.productData.baseData.currencies;

  let primaryLangId = primaryLanguageId;

  if (order) {
    primaryLangId = order.language_id;
  }

  const allowances = [
    ...terminalGroup
      .map((t) => {
        let allowancesItems = [...(t.terminalAllowances ?? [])];
        if (allowancesItems.length === 0 && !firstTerminal && !lastTerminal) {
          allowancesItems = [getIncludedAllowance(t.loadingType)];
        }
        return allowancesItems;
      })
      .flat(),
  ];

  const allowancesRowsData: RowProps[] = [];

  allowances.forEach((item: CalculatedAllowanceOffer) => {
    if (!item.allowanceOffer || item.allowanceType) {
      return null;
    }

    const baseCurrency = currencies.find(
      (c) => c.id === item.allowanceOfferCondition.currency_id
    ) as CurrencyData;

    const allowanceInstant = allowancesData.find(
      (a) => a.id === item.allowanceOffer.allowance_id
    ) as AllowanceData;

    const allowanceName = getLocalization(
      allowanceInstant.default_name,
      allowanceInstant.localized_names_data,
      primaryLangId
    );

    if (baseCurrency) {
      allowancesRowsData.push({
        price: item.calculatedPrice,
        name: allowanceName,
        baseCurrency,
        taxName: taxNames.length
          ? taxNames.find((i) => +i.taxId === item.allowanceOfferCondition.tax_id)?.localizedName ||
            ''
          : '',
        targetCurrency: currencyRoute,
      } as RowProps);
    }
    return null;
  });

  crossDockingOperations
    .filter((operation) => operation.terminalId.toString() === terminal.terminal.id)
    .forEach((operation) =>
      (operation.allowances ?? []).forEach((item) => {
        if (!item.allowanceOffer || item.allowanceType) {
          return null;
        }

        const baseCurrency = currencies.find(
          (c) => c.id === item.allowanceOfferCondition.currency_id
        ) as CurrencyData;

        const allowanceInstant = allowancesData.find(
          (a) => a.id === item.allowanceOffer.allowance_id
        ) as AllowanceData;

        const allowanceName = getLocalization(
          allowanceInstant.default_name,
          allowanceInstant.localized_names_data,
          primaryLangId
        );

        if (baseCurrency) {
          allowancesRowsData.push({
            price: item.calculatedPrice,
            name: allowanceName,
            baseCurrency,
            taxName: taxNames.length
              ? taxNames.find((i) => +i.taxId === item.allowanceOfferCondition.tax_id)
                  ?.localizedName || ''
              : '',
            targetCurrency: currencyRoute,
          } as RowProps);
        }
        return null;
      })
    );

  const handleGenerateLocalizedName = (defaultName: string, localizations: LocalizedMessage[]) => {
    const localization = localizations.find((l) => l.lang_id === primaryLangId);
    if (!localization || localization.message.length === 0) {
      return defaultName;
    }

    return localization.message;
  };

  const crossDockingRowsData: RowProps[] = crossDockingOperations
    .filter((operation) => operation.terminalId.toString() === terminal.terminal.id)
    .map((operation) => {
      return operation.priceParts.map((part) => {
        const baseCurrency = currencies.find(
          (c) => c.id === part.offerCondition.currency_id
        ) as CurrencyData;
        const unitGroup = unitGroups.find((u) => +u.id === part.unitGroup);
        return {
          price: operation.calculatedPrice,
          name: `${t('RoutesListComponent:CrossDocking')} (${t(
            'RoutesListComponent:CrossDockingCargoTypes.From.Text'
          )} ${t(`RoutesListComponent:CrossDockingCargoTypes.From.${operation.fromKind}`)} > ${t(
            'RoutesListComponent:CrossDockingCargoTypes.To.Text'
          )} ${t(
            `RoutesListComponent:CrossDockingCargoTypes.To.${operation.toKind}`
          )}), ${handleGenerateLocalizedName(
            unitGroup.default_name,
            unitGroup.localized_names_data
          )}`,
          baseCurrency,
          taxName: taxNames.length
            ? taxNames.find((i) => +i.taxId === part.offerCondition.tax_id)?.localizedName || ''
            : '',
          targetCurrency: currencyRoute,
        };
      });
    })
    .flat();

  const isIncluded =
    (!!firstTerminal && startTransportingConditionId === 2) ||
    (!!lastTerminal && endTransportingConditionId === 2) ||
    (!firstTerminal && !lastTerminal && terminal.terminalLoadingUnloadingOffer === null);

  if (totalPrice === 0) {
    return (
      <TerminalsInfoWithFullIncludedAllowance
        isIncluded={isIncluded}
        id={terminal.terminal.id}
        branchItem={terminals.find((t) => t.id === terminal.terminal.id)}
        firstTerminal={!!firstTerminal}
        lastTerminal={!!lastTerminal}
        order={order}
        crossDockingRowsData={crossDockingRowsData}
        currencyRoute={currencyRoute}
      />
    );
  }

  return (
    <TerminalsInfoWithAllowance
      {...props}
      allowancesRowsData={allowancesRowsData}
      crossDockingRowsData={crossDockingRowsData}
      order={order}
      crossDockingOperations={crossDockingOperations}
    />
  );
});

export default TerminalsGroupInfo;
